<template>
    <pendo-divider
        v-if="type === 'divider'"
        decorative
        :style="cssVars"
        class="multiselect-blade-option__group-divider"
        height="1px" />
    <div
        v-if="type === 'group'"
        :id="groupHeadingId"
        :style="cssVars"
        class="multiselect-blade-option__group-row multiselect-blade-option__row"
        :class="{ 'is-selected': value || indeterminate, 'is-single-select': isSingleSelect, 'is-invisible': invisible, 'visible': !invisible }">
        <span class="multiselect-blade-option__group-collapse-icon">
            <pendo-icon-button
                :icon="collapsed ? 'chevron-down' : 'chevron-up'"
                :tooltip="expandCollapseButtonLabel"
                :aria-label="expandCollapseButtonLabel"
                :aria-expanded="!collapsed"
                size="mini"
                type="tertiary"
                @click="onExpandedToggle" />
        </span>
        <div
            v-if="isSingleSelect"
            class="multiselect-blade-option__group-row__label"
            role="heading"
            aria-level="4"
            @focusin="onOptionMouseenter($event)"
            @mouseenter="onOptionMouseenter($event)"
            @focusout="onOptionMouseleave($event)"
            @mouseleave="onOptionMouseleave($event)">
            <pendo-icon
                v-if="color"
                class="multiselect-blade-option__group-swatch"
                :fill="color"
                size="14"
                stroke-width="0"
                type="square"
                :aria-hidden="true" />
            <span
                v-pendo-tooltip="{
                    content: capitalizeLabel,
                    multiline: true,
                    trigger: 'manual',
                    offset: 0,
                    show: showOverflowTooltip,
                }"
                class="multiselect-blade-option__group-label multiselect-blade-option__group-name">{{ capitalizeLabel }}</span>
        </div>
        <pendo-checkbox
            v-else
            v-pendo-tooltip="disabled && disabledTooltip"
            v-bind="{ value, disabled, indeterminate }"
            :label="capitalizeLabel"
            @change="handleGroupToggle">
            <div
                class="multiselect-blade-option__group-row__label">
                <pendo-icon
                    v-if="color"
                    class="multiselect-blade-option__group-swatch"
                    :fill="color"
                    size="14"
                    stroke-width="0"
                    type="square"
                    :aria-hidden="true" />
                <span class="multiselect-blade-option__group-name">{{ capitalizeLabel }}</span>
                <pendo-icon
                    v-if="beforeOptionsTooltip"
                    v-pendo-tooltip="{
                        trigger: 'hover',
                        arrow: true,
                        content: beforeOptionsTooltip,
                        multiline: true,
                    }"
                    class="multiselect-blade-option__before-options-tooltip-info"
                    :class="{ 'is-disabled': disabled }"
                    size="14"
                    display="inline"
                    type="info" />
            </div>
        </pendo-checkbox>
    </div>
    <div
        v-if="type === 'empty'"
        :style="cssVars"
        class="multiselect-blade-option__empty">
        <span>{{ label }}</span>
    </div>
    <div
        v-if="type === 'option'"
        v-pendo-tooltip="disabled && disabledTooltip ? disabledTooltip : false"
        :style="cssVars"
        @focusin="onOptionMouseenter($event)"
        @mouseenter="onOptionMouseenter($event)"
        @focusout="onOptionMouseleave($event)"
        @mouseleave="onOptionMouseleave($event)">
        <component
            :is="isSingleSelect ? 'pendo-radio' : 'pendo-checkbox'"
            v-if="type === 'option'"
            v-bind="{
                'data-cy': 'blade-option',
                style: cssVars,
                value: isSingleSelect ? itemId : value,
                disabled: disabled,
                checked: isSingleSelect ? undefined : value,
                'aria-describedby': optionAriaDescribedBy,
            }"
            class="multiselect-blade-option__row multiselect-blade-option__option-row"
            :class="{
                'is-selected': value,
                'is-disabled': disabled,
                'is-invisible': invisible,
                'visible': !invisible,
                'is-single-select': isSingleSelect,
            }"
            data-cy="blade-option"
            :name="groupId"
            @change="handleToggle">
            <div
                v-pendo-tooltip="{
                    content: sublabel ? `${label}\n\n${sublabel}` : label,
                    multiline: true,
                    trigger: 'manual',
                    offset: 0,
                    show: showOverflowTooltip,
                }"
                class="multiselect-blade-option__option-row__label">
                <pendo-app-icon
                    v-if="showAppIcon"
                    :app="{
                        applicationFlags: applicationFlags,
                        displayName: label,
                        trainingAttributes: trainingAttributes,
                    }" />
                <pendo-icon
                    v-else-if="optionIcon"
                    :type="optionIcon.type"
                    :fill="optionIcon.fill"
                    :stroke="optionIcon.stroke"
                    :size="16" />
                <div class="multiselect-blade-option-label-content">
                    <div class="multiselect-blade-option-label-text-row">
                        <span class="multiselect-blade-option-label-text">{{ label || '(No name)' }}</span>
                        <pendo-icon
                            v-if="isStarred"
                            type="star"
                            size="12"
                            :aria-hidden="true"
                            :fill="'var(--palette-yellow-50)'"
                            :stroke="'var(--palette-yellow-100)'" />
                    </div>
                    <div
                        v-if="sublabel"
                        class="multiselect-blade-option-label-subtext">
                        {{ sublabel }}
                    </div>
                </div>
            </div>
        </component>
    </div>
</template>

<script>
import { PendoCheckbox, PendoDivider, PendoTooltip, PendoIcon, PendoIconButton, PendoAppIcon, PendoRadio } from '@pendo/components';
import { elementHasOverflow } from '@/utils/dom.utils';
import { OPTION_HEIGHT, GROUP_OPTION_HEIGHT, SPACING_HEIGHT, DIVIDER_LINE_HEIGHT } from './multiselect-blade.utils';
import lowerFirst from 'lodash/lowerFirst';
import upperFirst from 'lodash/upperFirst';
import { groupColorMap } from '@/groups/group-color-picker/group-color-service';
import { AI_AGENT_ICON_TYPE } from '@/ai-agents/components/ai-agents.constants';

export default {
    name: 'MultiselectBladeOption',
    components: {
        PendoCheckbox,
        PendoDivider,
        PendoIconButton,
        PendoAppIcon,
        PendoIcon,
        PendoRadio,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        label: {
            type: String,
            default: '',
        },
        sublabel: {
            type: String,
            default: '',
        },
        value: {
            type: Boolean,
            default: false,
        },
        beforeOptionsTooltip: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        disabledTooltip: {
            type: String,
            default: '',
        },
        indeterminate: {
            type: Boolean,
            default: false,
        },
        invisible: {
            type: Boolean,
            default: false,
        },
        type: {
            type: String,
            default: 'option',
        },
        groupId: {
            type: String,
            default: undefined,
        },
        groupDescriptionId: {
            type: String,
            default: undefined,
        },
        itemId: {
            type: [String, Number],
            default: '',
        },
        icon: {
            type: [String, Object],
            default: '',
        },
        trainingAttributes: {
            type: Object,
            default: () => ({}),
        },
        applicationFlags: {
            type: Object,
            default: () => ({}),
        },
        color: {
            type: String,
            default: groupColorMap['.groupColorUngrouped'],
        },
        collapsed: {
            type: Boolean,
            default: false,
        },
        isSingleSelect: {
            type: Boolean,
            default: false,
        },
        isStarred: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['change', 'groupCollapse', 'mouseenter', 'mouseleave'],
    data () {
        return {
            showOverflowTooltip: false,
            tooltipTimeout: null,
            cssVars: {
                '--option-height': `${OPTION_HEIGHT}px`,
                '--group-height': `${GROUP_OPTION_HEIGHT}px`,
                '--spacing-height': `${SPACING_HEIGHT}px`,
                '--divider-line-height': `${DIVIDER_LINE_HEIGHT}px`,
            },
        };
    },
    computed: {
        groupHeadingId () {
            return this.type === 'group' && this.itemId ? `multiselect-blade-group-${this.itemId}` : undefined;
        },
        optionAriaDescribedBy () {
            if (this.type !== 'option') return undefined;
            if (this.groupDescriptionId) return this.groupDescriptionId;
            const hasRenderedGroupHeading = this.groupId && this.groupId !== 'blade-group';

            return hasRenderedGroupHeading ? `multiselect-blade-group-${this.groupId}` : undefined;
        },
        capitalizeLabel () {
            const capitalizeLabel = upperFirst(this.label);

            return capitalizeLabel;
        },
        expandCollapseButtonLabel () {
            const action = this.collapsed ? this.$t('Expand') : this.$t('Collapse');

            return `${action} ${this.capitalizeLabel}`;
        },
        optionIcon () {
            if (typeof this.icon === 'string') {
                if (this.icon === 'extension') {
                    return { type: 'puzzle-piece' };
                }
                if (this.icon === 'AIAgent') {
                    return { type: AI_AGENT_ICON_TYPE };
                }

                return { type: lowerFirst(this.icon) };
            }

            return this.icon;
        },
        showAppIcon () {
            return this.trainingAttributes.whiteLabelSettings?.logoUrl && this.applicationFlags.trainingApplication;
        },
    },
    methods: {
        handleToggle () {
            if (!this.disabled) {
                this.$emit('change', !this.value);
            }
        },
        handleGroupToggle () {
            if (this.isSingleSelect || this.disabled) return;

            return this.handleToggle();
        },
        onOptionMouseenter ($event) {
            const labelNodes = $event.currentTarget.querySelectorAll(
                this.type === 'group'
                    ? '.multiselect-blade-option__group-name'
                    : '.multiselect-blade-option-label-text, multiselect-blade-option-label-subtext',
            );
            const showOverflowTooltip = Array.from(labelNodes).some((node) => elementHasOverflow(node));

            if (showOverflowTooltip) {
                clearTimeout(this.tooltipTimeout);
                this.tooltipTimeout = setTimeout(() => {
                    this.showOverflowTooltip = true;
                }, 400);
            }
            this.$emit('mouseenter', $event);
        },
        onOptionMouseleave ($event) {
            clearTimeout(this.tooltipTimeout);
            this.showOverflowTooltip = false;
            this.$emit('mouseleave', $event);
        },
        onExpandedToggle () {
            this.$emit('groupCollapse', !this.collapsed);
        },
    },
};
</script>

<style lang="less" scoped>
.multiselect-blade-option {
    &__group-divider {
        margin: 0;
        margin-top: var(--spacing-height);
        border-top: var(--divider-line-height) solid var(--palette-gray-40);
    }

    &__empty {
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        margin: 0px 8px 24px 32px;
        color: var(--palette-gray-70);
    }

    &__group-row {
        height: var(--group-height);
        align-items: center;
        margin-top: var(--spacing-height);
        display: flex;
        gap: 8px;

        &__label {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 8px;
            min-width: 0;
        }

        :deep(.pendo-checkbox__label) {
            font-size: 14px;
            font-weight: 600;
            line-height: 21.5px;
            color: var(--palette-gray-100);
        }

        :deep(.pendo-radio__container) {
            align-items: center;
            width: 100%;
            height: 100%;
        }

        &.is-selected {
            background-color: var(--palette-gray-30);
        }
    }

    &__group-label {
        flex: 1 1 0;
        overflow-x: auto;
        align-items: center;
    }

    &__group-collapse-icon {
        padding-left: 8px;
        cursor: pointer;
    }

    &__group-color {
        min-width: 12px;
    }

    &__group-name {
        font-size: 14px;
        font-weight: 600;
        line-height: 21.5px;
        color: var(--palette-gray-100);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__before-options-tooltip-info {
        display: flex;
        align-items: center;
        margin-left: 4px;

        &.is-disabled {
            color: var(--palette-gray-70);
        }
    }

    &__group-swatch {
        display: flex;
    }

    &__option-row {
        padding-left: 32px;
        align-items: center;

        &__label {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 8px;
        }

        &.is-hover,
        &:hover {
            background-color: var(--palette-gray-20);
        }

        &.is-selected {
            background-color: var(--palette-gray-30);
        }

            :deep(.pendo-radio__inner) {
                display: none;
            }

            :deep(.pendo-radio__input) {
                opacity: 0;
                outline: none;
                position: absolute;
                z-index: -1;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                margin: 0;
           }

           :deep(.pendo-radio__container.is-focus) {
                transition: outline 0.05s cubic-bezier(0.25, 0.1, 0.25, 1);
                outline: var(--palette-gray-80) solid 2px;
                border-radius: 3px;
           }

        .multiselect-blade-option-label-content {
            display: flex;
            flex-direction: column;
            min-width: 0;

            .multiselect-blade-option-label-text-row {
                display: flex;
                align-items: center;
                gap: 6px;
            }

            .multiselect-blade-option-label-text,
            .multiselect-blade-option-label-subtext {
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
            }

            .multiselect-blade-option-label-subtext {
                font-size: 12px;
                color: var(--palette-gray-70);
            }
        }
    }

    &__row {
        height: var(--option-height);
        display: flex;
        cursor: pointer;

        :deep(.pendo-checkbox__label) {
            min-width: 0;
            text-wrap-mode: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        :deep(.pendo-radio__label) {
            min-width: 0;
        }

        :deep(.pendo-radio__container) {
            min-width: 0;
            align-items: center;
            width: 100%;
            height: 100%;
        }

        .pendo-checkbox {
            min-width: 0;
            align-items: center;
            width: 100%;
            height: 100%;
        }

        &.is-disabled {
            cursor: not-allowed;
        }

        &.is-invisible {
            visibility: hidden;
        }

        &.is-single-select {
            cursor: default;
        }
    }
}
</style>
